import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientAPI } from '../../services/api';
import {
  Phone, Key, Server, CheckCircle, XCircle, AlertCircle, Eye, EyeOff,
  Save, Trash2, RefreshCw, ExternalLink, Info
} from 'lucide-react';

const TelnyxSettingsPage = () => {
  const { clientUser } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [config, setConfig] = useState({
    api_key: '',
    connection_id: '',
    phone_number: '',
    enabled: false
  });
  
  const [currentConfig, setCurrentConfig] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await clientAPI.getTelnyxConfig(clientUser.id);
        if (response.data.has_config) {
          setCurrentConfig(response.data);
          setConfig({
            api_key: '',
            connection_id: response.data.connection_id || '',
            phone_number: response.data.phone_number || '',
            enabled: response.data.enabled
          });
        }
      } catch (err) {
        console.error('Error fetching Telnyx config:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const response = await clientAPI.getTelnyxStatus(clientUser.id);
        setStatus(response.data);
      } catch (err) {
        console.error('Error fetching Telnyx status:', err);
      }
    };

    if (clientUser?.id) {
      fetchConfig();
      fetchStatus();
    }
  }, [clientUser?.id]);

  const fetchConfig = async () => {
    try {
      const response = await clientAPI.getTelnyxConfig(clientUser.id);
      if (response.data.has_config) {
        setCurrentConfig(response.data);
        setConfig({
          api_key: '', // Don't show actual key
          connection_id: response.data.connection_id || '',
          phone_number: response.data.phone_number || '',
          enabled: response.data.enabled
        });
      }
    } catch (err) {
      console.error('Error fetching Telnyx config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await clientAPI.getTelnyxStatus(clientUser.id);
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching Telnyx status:', err);
    }
  };

  const handleSave = async () => {
    if (!config.api_key && !currentConfig?.has_config) {
      setError(t('telnyxSettings.apiKeyRequired'));
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const payload = {
        api_key: config.api_key || undefined,
        connection_id: config.connection_id,
        phone_number: config.phone_number,
        enabled: config.enabled
      };
      
      // Use update if config exists and no new API key, otherwise create new
      if (currentConfig?.has_config && !config.api_key) {
        await clientAPI.updateTelnyxConfig(clientUser.id, {
          connection_id: config.connection_id,
          phone_number: config.phone_number,
          enabled: config.enabled
        });
      } else {
        await clientAPI.saveTelnyxConfig(clientUser.id, payload);
      }
      
      setSuccess(t('telnyxSettings.saveSuccess'));
      fetchConfig();
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || t('telnyxSettings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await clientAPI.verifyTelnyxConfig(clientUser.id);
      if (response.data.verified) {
        setSuccess(t('telnyxSettings.verifySuccess'));
        fetchConfig();
        fetchStatus();
      } else {
        setError(response.data.error || t('telnyxSettings.verifyFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('telnyxSettings.verifyError'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('telnyxSettings.confirmDelete'))) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await clientAPI.deleteTelnyxConfig(clientUser.id);
      setSuccess(t('telnyxSettings.deleteSuccess'));
      setCurrentConfig(null);
      setConfig({
        api_key: '',
        connection_id: '',
        phone_number: '',
        enabled: false
      });
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || t('telnyxSettings.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="telnyx-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2">{t('telnyxSettings.title')}</h1>
        <p className="text-gray-400">{t('telnyxSettings.subtitle')}</p>
      </div>

      {/* Status Banner */}
      {status && (
        <div className={`mb-6 p-4 rounded-xl border ${
          status.using === 'client' 
            ? 'bg-green-500/10 border-green-500/30'
            : 'bg-blue-500/10 border-blue-500/30'
        }`}>
          <div className="flex items-center gap-3">
            {status.using === 'client' ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <Info className="w-5 h-5 text-blue-400" />
            )}
            <div>
              <p className={`font-medium ${
                status.using === 'client' ? 'text-green-400' : 'text-blue-400'
              }`}>
                {status.message}
              </p>
              {status.phone_number && (
                <p className="text-gray-400 text-sm mt-1">
                  {t('telnyxSettings.callerID')}: {status.phone_number}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-red-400">
            <XCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">{t('telnyxSettings.configTitle')}</h2>
          {currentConfig?.has_config && (
            <div className="flex items-center gap-2">
              {currentConfig.verified ? (
                <span className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  {t('telnyxSettings.verified')}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {t('telnyxSettings.notVerified')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Enable Toggle */}
        <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-white font-medium">{t('telnyxSettings.enableOwn')}</p>
              <p className="text-gray-400 text-sm">{t('telnyxSettings.enableDesc')}</p>
            </div>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="w-5 h-5 rounded border-gray-600 text-teal-400 focus:ring-teal-400"
              data-testid="telnyx-enable-toggle"
            />
          </label>
        </div>

        <div className="space-y-4">
          {/* API Key */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                {t('telnyxSettings.apiKey')}
              </div>
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={config.api_key}
                onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                placeholder={currentConfig?.api_key_masked || t('telnyxSettings.apiKeyPlaceholder')}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 pr-12"
                data-testid="telnyx-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-1">
              {t('telnyxSettings.apiKeyHelp')} 
              <a href="https://portal.telnyx.com/#/app/api-keys" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline ml-1">
                Telnyx Portal <ExternalLink className="w-3 h-3 inline" />
              </a>
            </p>
          </div>

          {/* Connection ID */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4" />
                {t('telnyxSettings.connectionId')}
              </div>
            </label>
            <input
              type="text"
              value={config.connection_id}
              onChange={(e) => setConfig({ ...config, connection_id: e.target.value })}
              placeholder={t('telnyxSettings.connectionIdPlaceholder')}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400"
              data-testid="telnyx-connection-id-input"
            />
            <p className="text-gray-500 text-xs mt-1">
              {t('telnyxSettings.connectionIdHelp')}
            </p>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('telnyxSettings.phoneNumber')}
              </div>
            </label>
            <input
              type="text"
              value={config.phone_number}
              onChange={(e) => setConfig({ ...config, phone_number: e.target.value })}
              placeholder="+1234567890"
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400"
              data-testid="telnyx-phone-input"
            />
            <p className="text-gray-500 text-xs mt-1">
              {t('telnyxSettings.phoneNumberHelp')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-800">
          <div className="flex gap-3">
            {currentConfig?.has_config && (
              <>
                <button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="telnyx-verify-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${verifying ? 'animate-spin' : ''}`} />
                  {t('telnyxSettings.verify')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="telnyx-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('telnyxSettings.useSystem')}
                </button>
              </>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-teal-400 hover:bg-teal-500 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid="telnyx-save-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-6 bg-gray-800/30 border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">{t('telnyxSettings.helpTitle')}</h3>
        <div className="space-y-3 text-gray-400 text-sm">
          <p>{t('telnyxSettings.help1')}</p>
          <p>{t('telnyxSettings.help2')}</p>
          <p>{t('telnyxSettings.help3')}</p>
        </div>
      </div>
    </div>
  );
};

export default TelnyxSettingsPage;
